import { Link } from 'react-router-dom';
import { Bell, User } from 'lucide-react';
import ThemeToggle from '../ThemeToggle';
import useAvatar from '../../hooks/useAvatar';
import { WebsiteWithConnections } from '../../types/website';
import { Button, IconButton } from '../ui';

interface WebsiteHeaderProps {
  website: WebsiteWithConnections | null;
  isEditMode: boolean;
  isSubmitting: boolean;
  formatUrl: (url: string) => string;
  setIsEditMode: (val: boolean) => void;
  handleSaveEdit: () => void;
  statusBadge?: React.ReactNode;
}

export default function WebsiteHeader({
  website,
  isEditMode,
  isSubmitting,
  formatUrl,
  setIsEditMode,
  handleSaveEdit,
  statusBadge,
}: WebsiteHeaderProps) {
  const avatarUrl = useAvatar();

  return (
    <header
      className="flex flex-col md:flex-row md:items-center justify-between gap-3"
      style={{
        padding: 'var(--space-5) 0',
        borderBottom: '1px solid var(--border-hairline)',
        marginBottom: 'var(--space-6)',
      }}
    >
      {/* Left: back + domain + status */}
      <div className="flex items-center" style={{ gap: '10px', minWidth: 0 }}>
        <Link to="/private-network" style={{ display: 'inline-flex' }}>
          <IconButton icon="chevron-left" size="sm" label="Back to Network" />
        </Link>
        <span className="t-eyebrow hidden sm:inline" style={{ flexShrink: 0 }}>Website</span>
        {/* Phones wrap a long domain instead of cutting it off */}
        <h1
          className="t-num md:truncate"
          style={{
            margin: 0,
            minWidth: 0,
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
            letterSpacing: 'var(--tracking-tight)',
            color: 'var(--text-body)',
            overflowWrap: 'anywhere',
          }}
        >
          {formatUrl(website?.url || '')}
        </h1>
        {statusBadge}
      </div>

      {/* Right: actions + chrome */}
      <div className="flex items-center justify-start md:justify-end" style={{ gap: '10px' }}>
        {!isEditMode ? (
          <>
            <Button size="sm" variant="secondary" icon="pencil" onClick={() => setIsEditMode(true)}>
              Edit
            </Button>
            <a
              href={website?.url.match(/^https?:\/\//) ? website.url : `https://${website?.url}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', textDecoration: 'none' }}
            >
              <Button size="sm" iconTrailing="arrow-up-right" nudge data-dir="up">
                Visit Site
              </Button>
            </a>
          </>
        ) : (
          <>
            <Button size="sm" variant="ghost" onClick={() => setIsEditMode(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button size="sm" icon="check" onClick={handleSaveEdit} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        )}

        <div className="hidden md:flex items-center" style={{ gap: '10px' }}>
          <ThemeToggle />
          <button
            className="ck-iconbtn pressable hover-wash"
            aria-label="Alerts"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'var(--control-md)',
              height: 'var(--control-md)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid transparent',
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'pointer',
            }}
          >
            <Bell size={16} />
          </button>
          <Link
            to="/settings"
            className="pressable"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--border-hairline)',
              background: avatarUrl ? 'var(--surface-raised)' : 'var(--accent)',
              color: 'var(--text-on-accent)',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="avatar"
                style={{ width: '100%', height: '100%', borderRadius: 'var(--radius-pill)', objectFit: 'cover' }}
              />
            ) : (
              <User size={15} />
            )}
          </Link>
        </div>
      </div>
    </header>
  );
}
